import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../utils/toast";
import { getBuildingWithRelations } from "../services/buildingService";
import { patchRoomLayout } from "../services/roomService";
import {
  buildingTypeOf,
  getSpaceTypes,
  roomSpaceTypes,
} from "../services/spaceCatalogService";
import {
  canSave,
  cellKey,
  isLockedRole,
  summariseFloor,
  validateFloor,
} from "../utils/floorLayoutValidation";
import "../styles/floor-layout-designer.css";

/**
 * Floor Layout Designer.
 *
 * Paint what each grid cell is — room, corridor, lobby, lift, stairs, void —
 * and say what each room is used for. The plan drives the 3D twin, so a layout
 * that would strand the walking agent cannot be saved: the checks panel and the
 * Save button are both driven by the same rules in floorLayoutValidation.js.
 *
 * The grid itself (rows x columns) belongs to the building and is not editable
 * here — resizing would delete rooms along with their tables and job
 * assignments, which is a destructive operation that deserves its own flow.
 */

/**
 * What a floor plan may paint. Lifts and stairs are NOT here: they are placed
 * once on the building itself and stamped onto every floor, which is what makes
 * a core line up from storey to storey. They appear in the legend as locked, so
 * the plan still reads, but they cannot be painted or painted over.
 */
const ROLES = [
  { id: "EMPTY", name: "Room", key: "1", hint: "An occupied space: offices, wards, classrooms." },
  { id: "CORRIDOR", name: "Corridor", key: "2", hint: "Circulation people walk along." },
  { id: "LOBBY", name: "Lobby", key: "3", hint: "Open entrance or atrium; also walkable." },
  { id: "VOID", name: "Void", key: "4", hint: "Open to the floor below; no floor here." },
];
const LOCKED_ROLES = [
  { id: "ELEVATOR", name: "Lift", hint: "Placed on the building, locked on every floor." },
  { id: "STAIRS", name: "Stairs", hint: "Placed on the building, locked on every floor." },
];
const ROLE_BY_ID = Object.fromEntries(
  [...ROLES, ...LOCKED_ROLES].map((r) => [r.id, r]),
);

/** Turn the API's building tree into the shape the validator works on. */
function toFloorModels(tree) {
  const rows = Number(tree?.rows) || 0;
  const columns = Number(tree?.columns) || 0;
  const floors = (tree?.floor ?? []).map((f) => {
    const cells = {};
    (f.room ?? []).forEach((room) => {
      cells[cellKey(room.row, room.column)] = {
        roomId: room.room_id,
        name: room.name,
        roomCode: room.roomCode,
        cellType: room.cellType || "EMPTY",
        spaceTypeId: room.space_type?.space_type_id ?? room.space_type_id ?? null,
        tableCount: (room.table ?? []).length,
      };
    });
    return {
      id: f.floor_id,
      name: f.name,
      code: f.floorCode,
      level: f.level ?? null,
      rows,
      columns,
      cells,
    };
  });
  // Ground floor first, using the explicit level where the CMS has one.
  floors.sort((a, b) => {
    if (a.level != null && b.level != null) return a.level - b.level;
    return String(a.code).localeCompare(String(b.code));
  });
  return floors;
}

export default function FloorLayoutDesignerPage({ buildingId, onBack }) {
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [building, setBuilding] = useState(null);
  const [buildingCells, setBuildingCells] = useState([]);
  const [floors, setFloors] = useState([]);
  const [activeFloorId, setActiveFloorId] = useState(null);
  const [spaceTypes, setSpaceTypes] = useState([]);
  const [tool, setTool] = useState("EMPTY");
  const [selected, setSelected] = useState(null); // [row, column]
  const [dirty, setDirty] = useState({}); // `${floorId}:${roomId}` -> true
  const [saving, setSaving] = useState(false);
  const [focusCells, setFocusCells] = useState([]); // cells an issue points at
  const painting = useRef(false);
  const history = useRef([]);

  // ------------------------------------------------------------ load
  const load = useCallback(async () => {
    if (!buildingId) return;
    setStatus("loading");
    setError("");
    try {
      const [tree, sts] = await Promise.all([
        getBuildingWithRelations(buildingId),
        getSpaceTypes().catch(() => []),
      ]);
      const model = toFloorModels(tree);
      setBuilding(tree);
      setBuildingCells(tree?.building_cell ?? []);
      setFloors(model);
      setActiveFloorId((prev) => prev ?? model[0]?.id ?? null);
      setSpaceTypes(sts);
      setDirty({});
      history.current = [];
      setStatus("ready");
    } catch (e) {
      setError(e?.message || "Could not load this building.");
      setStatus("error");
    }
  }, [buildingId]);

  useEffect(() => {
    load();
  }, [load]);

  const activeFloor = useMemo(
    () => floors.find((f) => f.id === activeFloorId) ?? null,
    [floors, activeFloorId],
  );

  const buildingType = useMemo(() => buildingTypeOf(building), [building]);

  // Rooms only. Circulation is painted as a role, not chosen as a purpose.
  const pickableSpaceTypes = useMemo(
    () => roomSpaceTypes(spaceTypes, buildingType?.code ?? null),
    [spaceTypes, buildingType],
  );

  // ------------------------------------------------------- validation
  const validation = useMemo(
    () => (activeFloor ? validateFloor(activeFloor, floors, buildingCells) : null),
    [activeFloor, floors, buildingCells],
  );
  const census = useMemo(
    () => (activeFloor ? summariseFloor(activeFloor) : null),
    [activeFloor],
  );

  // Saving is gated on EVERY floor, not just the one on screen: a broken
  // storey elsewhere would still strand the agent.
  const allFloorIssues = useMemo(
    () => floors.map((f) => ({ floor: f, ...validateFloor(f, floors, buildingCells) })),
    [floors, buildingCells],
  );
  const blockingFloors = allFloorIssues.filter((r) => !canSave(r.issues));
  const dirtyCount = Object.keys(dirty).length;

  const badCells = useMemo(() => {
    const set = new Set();
    (validation?.issues ?? [])
      .filter((i) => i.severity === "error")
      .forEach((i) => i.cells.forEach(([r, c]) => set.add(cellKey(r, c))));
    return set;
  }, [validation]);

  // ------------------------------------------------------------ edit
  const snapshot = useCallback(() => {
    history.current.push({ floors: JSON.parse(JSON.stringify(floors)), dirty: { ...dirty } });
    if (history.current.length > 50) history.current.shift();
  }, [floors, dirty]);

  const applyToCell = useCallback(
    (row, column, change) => {
      setFloors((prev) =>
        prev.map((f) => {
          if (f.id !== activeFloorId) return f;
          const key = cellKey(row, column);
          const cell = f.cells[key];
          if (!cell) return f;
          const next = { ...cell, ...change };
          if (next.cellType !== "EMPTY") next.spaceTypeId = null;
          if (
            next.cellType === cell.cellType &&
            next.spaceTypeId === cell.spaceTypeId
          ) {
            return f;
          }
          setDirty((d) => ({ ...d, [`${f.id}:${cell.roomId}`]: true }));
          return { ...f, cells: { ...f.cells, [key]: next } };
        }),
      );
    },
    [activeFloorId],
  );

  const paint = useCallback(
    (row, column) => {
      const cell = activeFloor?.cells?.[cellKey(row, column)];
      if (!cell || cell.cellType === tool) return;
      // Cores belong to the building. Repainting one here would leave this
      // floor disagreeing with every other storey.
      if (isLockedRole(cell.cellType)) {
        toast.error(
          `${ROLE_BY_ID[cell.cellType].name} cores are set on the building, not per floor. Change them in Edit Building.`,
        );
        return;
      }
      // Turning a furnished room into circulation would orphan its desks.
      if (tool !== "EMPTY" && cell.tableCount > 0) {
        toast.error(
          `${cell.name} has ${cell.tableCount} desk${cell.tableCount === 1 ? "" : "s"}. Remove them before changing it to ${ROLE_BY_ID[tool].name.toLowerCase()}.`,
        );
        return;
      }
      applyToCell(row, column, { cellType: tool });
    },
    [activeFloor, tool, applyToCell],
  );

  const undo = useCallback(() => {
    const prev = history.current.pop();
    if (!prev) return;
    setFloors(prev.floors);
    setDirty(prev.dirty);
  }, []);

  useEffect(() => {
    const onUp = () => {
      painting.current = false;
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (/^(INPUT|SELECT|TEXTAREA)$/.test(tag || "")) return;
      const role = ROLES.find((r) => r.key === e.key);
      if (role) setTool(role.id);
      if (e.key === "Escape") setSelected(null);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo]);

  // ------------------------------------------------------------ save
  const handleSave = async () => {
    if (blockingFloors.length) {
      toast.error("Fix the layout problems before saving.");
      return;
    }
    const edits = [];
    floors.forEach((f) =>
      Object.values(f.cells).forEach((cell) => {
        if (dirty[`${f.id}:${cell.roomId}`]) {
          edits.push({ floorName: f.name, cell });
        }
      }),
    );
    if (!edits.length) {
      toast("Nothing to save yet.");
      return;
    }
    setSaving(true);
    const results = await Promise.allSettled(
      edits.map((e) =>
        patchRoomLayout(e.cell.roomId, {
          cellType: e.cell.cellType,
          space_type_id: e.cell.spaceTypeId ?? null,
        }),
      ),
    );
    const failed = results.filter((r) => r.status === "rejected");
    setSaving(false);
    if (failed.length) {
      toast.error(
        `${failed.length} of ${edits.length} changes could not be saved. Nothing else was lost — try again.`,
      );
      return;
    }
    toast.success(`Saved ${edits.length} cell${edits.length === 1 ? "" : "s"}.`);
    setDirty({});
    load();
  };

  // ---------------------------------------------------------- render
  if (status === "loading") {
    return (
      <div className="fld-page">
        <p className="fld-muted" role="status">
          Loading the building…
        </p>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="fld-page">
        <div className="fld-error" role="alert">
          <span>{error}</span>
          <button type="button" className="fld-btn" onClick={load}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (!activeFloor) {
    return (
      <div className="fld-page">
        <p className="fld-muted">This building has no floors yet. Add one before designing a layout.</p>
      </div>
    );
  }

  const selectedCell = selected ? activeFloor.cells[cellKey(selected[0], selected[1])] : null;

  return (
    <div className="fld-page">
      <header className="fld-topbar">
        <div className="fld-brand">
          <h1>Floor Layout Designer</h1>
          <p>
            {building?.name}
            {building?.building_type?.name ? ` · ${building.building_type.name}` : ""}
            {` · ${activeFloor.rows}×${activeFloor.columns} grid`}
          </p>
        </div>

        <div className="fld-field">
          <label className="fld-label" htmlFor="fld-floor">
            Floor
          </label>
          <select
            id="fld-floor"
            value={activeFloorId}
            onChange={(e) => {
              setActiveFloorId(Number(e.target.value));
              setSelected(null);
              setFocusCells([]);
            }}
          >
            {floors.map((f) => {
              const broken = allFloorIssues.find((r) => r.floor.id === f.id && !canSave(r.issues));
              return (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.level != null ? ` · level ${f.level}` : ""}
                  {broken ? "  — needs fixing" : ""}
                </option>
              );
            })}
          </select>
        </div>

        <div className="fld-spacer" />
        {dirtyCount > 0 && (
          <span className="fld-dirty" aria-live="polite">
            {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
          </span>
        )}
        {onBack && (
          <button type="button" className="fld-btn" onClick={onBack}>
            Back
          </button>
        )}
        <button type="button" className="fld-btn" onClick={undo}>
          Undo
        </button>
        <button
          type="button"
          className="fld-btn fld-primary"
          onClick={handleSave}
          disabled={saving || blockingFloors.length > 0 || dirtyCount === 0}
          aria-describedby={blockingFloors.length ? "fld-save-block" : undefined}
        >
          {saving ? "Saving…" : "Save layout"}
        </button>
      </header>

      {blockingFloors.length > 0 && (
        <p className="fld-blocked" id="fld-save-block" role="status">
          Saving is off until the layout works:{" "}
          {blockingFloors.map((b) => b.floor.name).join(", ")} would leave people unable to reach
          part of the building.
        </p>
      )}

      <div className="fld-main">
        {/* The legend you read the plan by is the control you paint with. */}
        <aside className="fld-panel">
          <div className="fld-group">
            <h2 className="fld-label">Paint with</h2>
            <div className="fld-tools" role="group" aria-label="Cell role to paint">
              {ROLES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={`fld-tool${tool === r.id ? " is-active" : ""}`}
                  aria-pressed={tool === r.id}
                  onClick={() => setTool(r.id)}
                >
                  <span className={`fld-sw fld-sw-${r.id.toLowerCase()}`} aria-hidden="true" />
                  <span className="fld-tool-name">{r.name}</span>
                  <span className="fld-key" aria-hidden="true">
                    {r.key}
                  </span>
                </button>
              ))}
            </div>
            <p className="fld-hint">{ROLE_BY_ID[tool].hint}</p>
            <p className="fld-hint">
              Click a cell to paint it, or drag across several. Keys 1–4 switch tool, Ctrl+Z undoes.
            </p>
          </div>

          <div className="fld-group">
            <h2 className="fld-label">Set on the building</h2>
            <ul className="fld-locked-list">
              {LOCKED_ROLES.map((r) => (
                <li key={r.id} className="fld-locked">
                  <span className={`fld-sw fld-sw-${r.id.toLowerCase()}`} aria-hidden="true" />
                  <span className="fld-tool-name">{r.name}</span>
                  <span className="fld-lock" aria-label="locked">
                    Locked
                  </span>
                </li>
              ))}
            </ul>
            <p className="fld-hint">
              Cores are placed once in Edit Building and stamped onto every floor, so they line up
              from storey to storey. This plan's job is to put a corridor beside them.
            </p>
          </div>

          <div className="fld-group">
            <h2 className="fld-label">This floor</h2>
            <div className="fld-stats">
              <div className="fld-stat">
                <span className="fld-n">{census.rooms.length}</span>
                <span className="fld-l">Rooms</span>
              </div>
              <div className="fld-stat">
                <span className="fld-n">{census.circulation.length}</span>
                <span className="fld-l">Circulation</span>
              </div>
              <div className="fld-stat">
                <span className="fld-n">{census.shafts.length}</span>
                <span className="fld-l">Cores</span>
              </div>
              <div className="fld-stat">
                <span className="fld-n">{census.voids.length}</span>
                <span className="fld-l">Voids</span>
              </div>
            </div>
          </div>
        </aside>

        <section className="fld-canvas">
          <div className="fld-canvas-head">
            <h2>{activeFloor.name}</h2>
            <span className="fld-hint">
              Row and column numbers match the coordinates stored against each room.
            </span>
          </div>

          <div className="fld-plan-scroll">
            <div
              className="fld-plan"
              role="grid"
              aria-label={`${activeFloor.name} layout grid`}
              style={{ gridTemplateColumns: `32px repeat(${activeFloor.columns}, 64px)` }}
            >
              <span className="fld-ruler" aria-hidden="true" />
              {Array.from({ length: activeFloor.columns }, (_, i) => (
                <span key={`c${i}`} className="fld-ruler" aria-hidden="true">
                  {i + 1}
                </span>
              ))}
              {Array.from({ length: activeFloor.rows }, (_, ri) => {
                const row = ri + 1;
                return (
                  <React.Fragment key={`r${row}`}>
                    <span className="fld-ruler" aria-hidden="true">
                      {row}
                    </span>
                    {Array.from({ length: activeFloor.columns }, (_, ci) => {
                      const column = ci + 1;
                      const key = cellKey(row, column);
                      const cell = activeFloor.cells[key];
                      if (!cell) {
                        return (
                          <span key={key} className="fld-cell fld-missing" title="No room record">
                            —
                          </span>
                        );
                      }
                      const role = ROLE_BY_ID[cell.cellType] ?? ROLE_BY_ID.EMPTY;
                      const bad = badCells.has(key);
                      const focused = focusCells.some(([r, c]) => r === row && c === column);
                      const isSel = selected && selected[0] === row && selected[1] === column;
                      const locked = isLockedRole(cell.cellType);
                      const spaceName = pickableSpaceTypes.find(
                        (s) => s.space_type_id === cell.spaceTypeId,
                      )?.name;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="gridcell"
                          className={
                            `fld-cell fld-${cell.cellType.toLowerCase()}` +
                            (isSel ? " is-selected" : "") +
                            (bad ? " is-bad" : "") +
                            (focused ? " is-focus" : "") +
                            (locked ? " is-locked" : "")
                          }
                          aria-label={
                            `${role.name} at row ${row} column ${column}` +
                            (spaceName ? `, ${spaceName}` : "") +
                            (locked ? ", locked; set on the building" : "") +
                            (bad ? ", cannot be reached" : "")
                          }
                          onMouseDown={() => {
                            snapshot();
                            painting.current = true;
                            setSelected([row, column]);
                            paint(row, column);
                          }}
                          onMouseEnter={() => {
                            if (painting.current) paint(row, column);
                          }}
                        >
                          {bad && <span className="fld-flag" aria-hidden="true" />}
                          {locked && (
                            <span className="fld-lock-pip" aria-hidden="true" title="Locked" />
                          )}
                          <span className="fld-ct">
                            {cell.cellType === "EMPTY" ? "ROOM" : cell.cellType}
                          </span>
                          {spaceName && <span className="fld-st">{spaceName}</span>}
                          {cell.tableCount > 0 && (
                            <span className="fld-desks" aria-hidden="true">
                              {cell.tableCount} desk{cell.tableCount === 1 ? "" : "s"}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </section>

        <aside className="fld-panel">
          <div className="fld-group">
            <h2 className="fld-label">Selected space</h2>
            {!selectedCell ? (
              <p className="fld-muted">Select a cell on the plan to set what it is used for.</p>
            ) : (
              <>
                <dl className="fld-kv">
                  <div>
                    <dt>Room</dt>
                    <dd>{selectedCell.name}</dd>
                  </div>
                  <div>
                    <dt>Grid cell</dt>
                    <dd className="fld-mono">
                      R{selected[0]} · C{selected[1]}
                    </dd>
                  </div>
                  <div>
                    <dt>Role</dt>
                    <dd>{(ROLE_BY_ID[selectedCell.cellType] ?? ROLE_BY_ID.EMPTY).name}</dd>
                  </div>
                </dl>
                {selectedCell.cellType === "EMPTY" ? (
                  <div className="fld-field">
                    <label className="fld-label" htmlFor="fld-space">
                      Used as
                    </label>
                    <select
                      id="fld-space"
                      value={selectedCell.spaceTypeId ?? ""}
                      onChange={(e) => {
                        snapshot();
                        applyToCell(selected[0], selected[1], {
                          spaceTypeId: e.target.value ? Number(e.target.value) : null,
                        });
                      }}
                    >
                      <option value="">Not set</option>
                      {pickableSpaceTypes.map((s) => (
                        <option key={s.space_type_id} value={s.space_type_id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    {buildingType ? (
                      <p className="fld-hint">
                        Filtered to <b>{buildingType.name}</b> spaces, plus the
                        ones every building has. Corridors and lobbies are
                        painted as a role, not chosen here.
                      </p>
                    ) : (
                      <p className="fld-note" role="status">
                        This building has no building type, so every space in
                        the catalogue is listed. Set one in Edit Building to
                        narrow this to the spaces that kind of building
                        actually has.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="fld-hint">
                    {(ROLE_BY_ID[selectedCell.cellType] ?? ROLE_BY_ID.EMPTY).hint}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="fld-group">
            <h2 className="fld-label">Layout checks</h2>
            {validation.issues.length === 0 ? (
              <div className="fld-check fld-ok">
                <span className="fld-dot" aria-hidden="true" />
                <span>
                  <b>This floor works</b>
                  <span className="fld-fix">
                    Every room and core can be reached, and the circulation is continuous.
                  </span>
                </span>
              </div>
            ) : (
              validation.issues.map((issue) => (
                <div
                  key={issue.code}
                  className={`fld-check ${issue.severity === "error" ? "fld-bad" : "fld-warn"}`}
                >
                  <span className="fld-dot" aria-hidden="true" />
                  <span>
                    <b>{issue.title}</b>
                    <span className="fld-fix">{issue.detail}</span>
                    {issue.cells.length > 0 && (
                      <button
                        type="button"
                        className="fld-link"
                        onClick={() => setFocusCells(issue.cells)}
                      >
                        Show me on the plan
                      </button>
                    )}
                  </span>
                </div>
              ))
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
